import { motion } from "framer-motion";
import { ArrowUpRight, Copy, Check } from "lucide-react";
import { useState } from "react";
import { SITE } from "../data/site";

export default function Contact() {
  const [copied, setCopied] = useState(false);

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(SITE.email);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      console.error(e);
    }
  };

  const channels = [
    {
      id: "email",
      label: "Email",
      value: SITE.email,
      href: `mailto:${SITE.email}`,
    },
    {
      id: "whatsapp",
      label: "WhatsApp",
      value: SITE.whatsappDisplay,
      href: SITE.whatsappLink,
    },
    {
      id: "linkedin",
      label: "LinkedIn",
      value: "in/mohamad-ali-hama",
      href: SITE.linkedin,
    },
  ];

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative pt-24 md:pt-40 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="flex items-center gap-3 mb-10"
        >
          <span className="w-10 h-px bg-[#f5e6d3]" />
          <span className="font-display text-xs uppercase tracking-[0.3em] text-[#f5e6d3]">
            Contact — 006
          </span>
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9 }}
          data-testid="contact-headline"
          className="font-display uppercase leading-[0.85] tracking-tighter text-[16vw] md:text-[12vw] lg:text-[10vw]"
        >
          Let's
          <br />
          <span className="font-editorial italic font-light normal-case lowercase text-[#f5e6d3]">
            talk.
          </span>
        </motion.h2>

        <div className="mt-12 md:mt-20 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <p className="font-body text-base md:text-lg text-zinc-300 leading-relaxed max-w-md">
              Got a project, a half-formed idea, or an old site that needs a
              second life? Send a message — I usually reply within a day.
            </p>
            <div className="mt-8 font-editorial italic text-zinc-500">
              Based in {SITE.location}. Available worldwide.
            </div>
          </div>

          <div className="md:col-span-7 divide-y divide-white/10 border-y border-white/10">
            {channels.map((c) => (
              <a
                key={c.id}
                data-testid={`contact-${c.id}`}
                href={c.href}
                target={c.id === "email" ? undefined : "_blank"}
                rel="noopener noreferrer"
                className="group flex items-center justify-between gap-6 py-6 md:py-8 hover:px-3 transition-all duration-500"
              >
                <div className="flex flex-col">
                  <span className="font-display text-xs uppercase tracking-[0.25em] text-zinc-500 mb-1">
                    {c.label}
                  </span>
                  <span className="font-display text-2xl md:text-4xl uppercase tracking-tight group-hover:text-[#f5e6d3] transition-colors">
                    {c.value}
                  </span>
                </div>
                <ArrowUpRight
                  size={32}
                  className="text-zinc-500 group-hover:text-[#f5e6d3] group-hover:rotate-45 transition-all duration-500 shrink-0"
                />
              </a>
            ))}
          </div>
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-4">
          <a
            data-testid="contact-cta-mail"
            href={`mailto:${SITE.email}`}
            className="font-display text-xs uppercase tracking-[0.25em] bg-[#f5e6d3] text-black px-7 py-4 rounded-full hover:bg-white transition-colors inline-flex items-center gap-3"
          >
            Send an email
            <ArrowUpRight size={14} />
          </a>
          <button
            data-testid="contact-copy-email"
            onClick={copyEmail}
            className="font-display text-xs uppercase tracking-[0.25em] border border-white/20 px-7 py-4 rounded-full hover:border-white hover:bg-white/5 transition-colors inline-flex items-center gap-3"
          >
            {copied ? (
              <>
                <Check size={14} /> Copied
              </>
            ) : (
              <>
                <Copy size={14} /> Copy email
              </>
            )}
          </button>
        </div>
      </div>
    </section>
  );
}
